import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    deleteSoftwareLicences,
    hydrateSoftwareLicencesSuccess,
    loadSoftwareLicences,
    loadSoftwareLicencesError,
    loadSoftwareLicencesSuccess,
} from '../actions/software-licences.actions';
import {
    initialSoftwareLicencesState,
    SoftwareLicencesCollectionState,
    SoftwareLicencesEntry,
} from '../software-licences-collection.state';
import { isDefined } from '../../../utilities/is-defined';

const setSoftwareLicencesLoadingReducer = (
    state: Readonly<SoftwareLicencesCollectionState>,
    { equipmentKey }: { equipmentKey: string },
): Readonly<SoftwareLicencesCollectionState> => ({
    ...state,
    licences: {
        ...state.licences,
        [equipmentKey]: { data: state.licences[equipmentKey]?.data ?? [], status: LoadStatus.Loading },
    },
});

const setSoftwareLicencesLoadedReducer = (
    state: Readonly<SoftwareLicencesCollectionState>,
    { equipmentKey, data }: { equipmentKey: string; data: SoftwareLicencesEntry['data'] },
): Readonly<SoftwareLicencesCollectionState> => ({
    ...state,
    licences: {
        ...state.licences,
        [equipmentKey]: { data, status: LoadStatus.Loaded },
    },
});

const setLoadedFromStorageReducer = (
    state: Readonly<SoftwareLicencesCollectionState>,
    { data }: { data: Record<string, SoftwareLicencesEntry['data']> | undefined },
): Readonly<SoftwareLicencesCollectionState> => {
    const licencesFromStorage: Record<string, SoftwareLicencesEntry> = {};

    if (!isDefined(data)) {
        return state;
    }

    Object.entries(data).forEach(([equipmentKey, entry]) => {
        licencesFromStorage[equipmentKey] = {
            data: entry,
            status: LoadStatus.Loaded,
        };
    });

    return {
        ...state,
        licences: licencesFromStorage,
    };
};

const setFailedReducer = (
    state: Readonly<SoftwareLicencesCollectionState>,
    { equipmentKey }: { equipmentKey: string },
): Readonly<SoftwareLicencesCollectionState> => ({
    ...state,
    licences: {
        ...state.licences,
        [equipmentKey]: {
            data: state.licences[equipmentKey]?.data ?? [],
            status: isDefined(state.licences[equipmentKey]?.data) ? LoadStatus.Loaded : LoadStatus.Error,
        },
    },
});

const deleteEntryReducer = (
    state: Readonly<SoftwareLicencesCollectionState>,
    { equipmentKeys }: { equipmentKeys: string[] },
): Readonly<SoftwareLicencesCollectionState> => {
    const newStateLicences = { ...state.licences };

    equipmentKeys.forEach((equipmentKey) => delete newStateLicences[equipmentKey]);

    return {
        ...state,
        licences: {
            ...newStateLicences,
        },
    };
};

const createSoftwareLicencesReducer = createReducer(
    initialSoftwareLicencesState,
    on(loadSoftwareLicences, setSoftwareLicencesLoadingReducer),
    on(loadSoftwareLicencesSuccess, setSoftwareLicencesLoadedReducer),
    on(hydrateSoftwareLicencesSuccess, setLoadedFromStorageReducer),
    on(loadSoftwareLicencesError, setFailedReducer),
    on(deleteSoftwareLicences, deleteEntryReducer),
);

export function softwareLicencesReducer(
    state: Readonly<SoftwareLicencesCollectionState> | undefined,
    action: Action,
): Readonly<SoftwareLicencesCollectionState> {
    return createSoftwareLicencesReducer(state, action);
}
